import React, { ReactNode } from "react";

import { useSelector } from "react-redux";

import {
  ConsignableSaleTypes,
  LivestockAuctionAndClearanceSaleTypes,
  LivestockAuctionAndPaddockSaleTypes,
  LivestockAuctionSaleTypes,
  LivestockSaleTypes,
  SaleType,
  SaleyardSaleTypes,
  TransactionalSaleTypes,
} from "constants/sale";

import { currentSaleSelector } from "selectors";

interface ForSaleTypeProps {
  children: ReactNode;
  exceptSaleType?: string[];
  saleType?: string[];
  selectedSaleType?: string;
}

interface ForSelectedSaleTypeProps {
  children: ReactNode;
  selectedSaleType?: string;
}

/**
 * ForSaleType is a functional component that conditionally renders its children based on the given sale type restrictions
 * and the current application state. It provides a mechanism to enforce visibility rules for content based on sale type conditions.
 */
export const ForSaleType = ({
  selectedSaleType = null,
  saleType = [],
  exceptSaleType = [],
  children,
}: ForSaleTypeProps) => {
  let stateSaleType = useSelector(state =>
    currentSaleSelector(state),
  )?.sale_type;

  if (selectedSaleType) {
    stateSaleType = selectedSaleType;
  } else if (!stateSaleType) {
    return null;
  }

  const whiteListCheck =
    saleType.length > 0 ? saleType.includes(stateSaleType) : true;
  const blackListCheck =
    exceptSaleType.length > 0 ? !exceptSaleType.includes(stateSaleType) : true;

  const visible = whiteListCheck && blackListCheck;

  return (visible && children) || null;
};

export const ForSaleyardAuction = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType selectedSaleType={selectedSaleType} saleType={SaleyardSaleTypes}>
    {children}
  </ForSaleType>
);

export const ForLivestockAuctionAndPaddock = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={LivestockAuctionAndPaddockSaleTypes}
  >
    {children}
  </ForSaleType>
);

export const ForLivestockAuction = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={LivestockAuctionSaleTypes}
  >
    {children}
  </ForSaleType>
);

export const ForConsignableSales = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={ConsignableSaleTypes}
  >
    {children}
  </ForSaleType>
);

export const ForLivestockSale = ({ children }: Partial<ForSaleTypeProps>) => (
  <ForSaleType saleType={LivestockSaleTypes}>{children}</ForSaleType>
);

export const ForHooks = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.OVER_HOOKS]}
  >
    {children}
  </ForSaleType>
);

export const ForPaddock = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.PADDOCK]}
  >
    {children}
  </ForSaleType>
);

export const ForHooksOrPaddock = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={TransactionalSaleTypes}
  >
    {children}
  </ForSaleType>
);

export const ForNotHooksOrPaddock = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={TransactionalSaleTypes}
  >
    {children}
  </ForSaleType>
);

export const ForHooksOrPaddockOrExternalAgentSales = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[
      SaleType.EXTERNAL_AGENT_SALE,
      SaleType.SELL_VIA_EXTERNAL_AGENT,
      ...TransactionalSaleTypes,
    ]}
  >
    {children}
  </ForSaleType>
);

export const ForNotHooksOrPaddockOrExternalAgentSales = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[
      SaleType.EXTERNAL_AGENT_SALE,
      SaleType.SELL_VIA_EXTERNAL_AGENT,
      ...TransactionalSaleTypes,
    ]}
  >
    {children}
  </ForSaleType>
);

export const ForNotHooks = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[SaleType.OVER_HOOKS]}
  >
    {children}
  </ForSaleType>
);

export const ForClearingSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.CLEARING]}
  >
    {children}
  </ForSaleType>
);

export const ForNotClearingSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[SaleType.CLEARING]}
  >
    {children}
  </ForSaleType>
);

export const ForNotPrivateWeigh = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[SaleType.PRIVATE_WEIGH]}
  >
    {children}
  </ForSaleType>
);

export const ForExternalAgentSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.EXTERNAL_AGENT_SALE, SaleType.SELL_VIA_EXTERNAL_AGENT]}
  >
    {children}
  </ForSaleType>
);

export const ForSellViaExternalAgent = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.SELL_VIA_EXTERNAL_AGENT]}
  >
    {children}
  </ForSaleType>
);

export const ForNotExternalAgentSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[
      SaleType.EXTERNAL_AGENT_SALE,
      SaleType.SELL_VIA_EXTERNAL_AGENT,
    ]}
  >
    {children}
  </ForSaleType>
);

export const ForNotExternalAgentSaleNorClearingSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[
      SaleType.EXTERNAL_AGENT_SALE,
      SaleType.SELL_VIA_EXTERNAL_AGENT,
      SaleType.CLEARING,
    ]}
  >
    {children}
  </ForSaleType>
);

export const ForReferenceSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={[SaleType.REFERENCE_SALE]}
  >
    {children}
  </ForSaleType>
);

export const ForNotReferenceSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    exceptSaleType={[SaleType.REFERENCE_SALE]}
  >
    {children}
  </ForSaleType>
);

export const ForLivestockAuctionAndClearingSale = ({
  selectedSaleType = null,
  children,
}: ForSelectedSaleTypeProps) => (
  <ForSaleType
    selectedSaleType={selectedSaleType}
    saleType={LivestockAuctionAndClearanceSaleTypes}
  >
    {children}
  </ForSaleType>
);
